<template>
  <vs-wrapper v-if="shouldShowFormRequirements">
    <v-row data-test="form-requirement-edit-form">
      <v-col class="shrink">
        <vs-heading type="overline">Your forms</vs-heading>
      </v-col>
      <v-col class="pt-2 pl-1">
        <v-tooltip bottom max-width="380">
          <template #activator="{ on }">
            <v-icon small v-on="on">info</v-icon>
          </template>
          <span>
            Field workers are required to fill your forms. Submissions are only visible within your
            company.
          </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <vs-wrapper space="small">
      <v-card
        v-for="formRequirement in formRequirements"
        :key="formRequirement.id"
        rounded="lg"
        full-width
        outlined
      >
        <v-row :data-test="`form-requirement-row-${formRequirement.id}`" class="pa-2" dense>
          <v-col class="shrink" align-self="center">
            <v-icon small :color="getFormIconColor(formRequirement)"
              >{{ getFormIcon(formRequirement) }}
            </v-icon>
          </v-col>
          <v-col class="grow" align-self="center">
            <vs-text>{{ getFormName(formRequirement.id) }}</vs-text>
          </v-col>
          <v-col class="shrink">
            <vs-button
              v-if="canFillForm(formRequirement)"
              label="Fill out"
              type="secondary"
              small
              :data-test="`form-requirement-fill-button-${formRequirement.id}`"
              @click="fillFormRequirement(formRequirement)"
            />
            <vs-button
              v-else-if="canViewFormSubmission(formRequirement)"
              label="View"
              type="secondary"
              small
              :data-test="`form-requirement-view-button-${formRequirement.id}`"
              @click="viewFormRequirementSubmission(formRequirement)"
            />
            <vs-button
              v-if="!readonly"
              :data-test="`form-requirement-remove-button-${formRequirement.id}`"
              icon="clear"
              small
              destructive
              type="tertiary"
              @click="removeFormRequirement(formRequirement.id)"
            />
          </v-col>
        </v-row>
      </v-card>

      <vs-button
        v-if="!readonly"
        data-test="form-requirement-add-button"
        icon="mdi-clipboard-text-outline"
        label="Add form requirement"
        type="secondary"
        full-width
        small
        @click="openFormSelectionBottomSheet"
      />
    </vs-wrapper>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { getInstance } from '@/auth'
import { computed } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import JobAssignmentFormRequirement from '@/models/job-assignment-form-requirement'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'

interface Props {
  formRequirements: JobAssignmentFormRequirement[]
  serviceProviderBusinessUnitId?: string | null
  hubId: string
  isNewJob?: boolean
  readonly?: boolean
}

interface AssignmentFormRequirementViewModel {
  id: string
  formName: string
  occurence: number
}

const props = withDefaults(defineProps<Props>(), {
  serviceProviderBusinessUnitId: null,
  isNewJob: false,
  readonly: false,
})

const emit = defineEmits<{
  (e: 'update-form-requirements', requirements: JobAssignmentFormRequirement[]): void
  (e: 'show-assignment-form-selection'): void
  (e: 'fill-form-requirement', requirement: JobAssignmentFormRequirement): void
  (e: 'view-form-requirement-submission', requirement: JobAssignmentFormRequirement): void
}>()

const auth = getInstance()

const { getById: getFormById } = useGetters('forms', ['getById'])
const { getBusinessUnitById } = useGetters('businessUnits', ['getBusinessUnitById'])

const shouldShowFormRequirements = computed(() => {
  if (props.serviceProviderBusinessUnitId === null) return false

  const userBusinessUnitId = auth.organizationId
  const hub = getBusinessUnitById.value(props.hubId)

  const userIsInServiceProvider = props.serviceProviderBusinessUnitId === userBusinessUnitId
  const isAssignedToHubAndUserIsInHub =
    !!hub?.ownerId &&
    props.serviceProviderBusinessUnitId === props.hubId &&
    hub.ownerId === userBusinessUnitId
  return userIsInServiceProvider || isAssignedToHubAndUserIsInHub
})

const canFillForms = computed(() => {
  return !props.isNewJob
})

const formRequirementNames = computed(() => {
  const mappedRequirements: Array<AssignmentFormRequirementViewModel> = []
  props.formRequirements.forEach((requirement) => {
    const form = getFormById.value(requirement.formId)
    const formName = form?.name ?? 'Unknown form'
    let occurence = 1
    for (let index = mappedRequirements.length - 1; index >= 0; index--) {
      if (mappedRequirements[index].formName === formName) {
        occurence = mappedRequirements[index].occurence + 1
        break
      }
    }

    mappedRequirements.push({
      id: requirement.id,
      formName,
      occurence,
    })
  })

  return mappedRequirements
})

function openFormSelectionBottomSheet() {
  emit('show-assignment-form-selection')
}

function removeFormRequirement(id: string) {
  const formRequirements = [...props.formRequirements]
  const newFormRequirements = formRequirements.filter((requirement) => requirement.id !== id)

  emit('update-form-requirements', newFormRequirements)
}

function getFormName(requirementId: string): string {
  const model = formRequirementNames.value.find((requirement) => requirement.id === requirementId)
  if (!model) return ''
  return model.occurence > 1 ? `${model.formName} (${model.occurence})` : model.formName
}

function fillFormRequirement(requirement: JobAssignmentFormRequirement) {
  emit('fill-form-requirement', requirement)
}

function viewFormRequirementSubmission(requirement: JobAssignmentFormRequirement) {
  emit('view-form-requirement-submission', requirement)
}

function getFormIcon(requirement: JobAssignmentFormRequirement) {
  return requirement.formSubmissionId === null
    ? 'mdi-clipboard-text-outline'
    : 'mdi-clipboard-check-outline'
}

function getFormIconColor(requirement: JobAssignmentFormRequirement) {
  return requirement.formSubmissionId !== null ? 'green' : null
}

function canFillForm(requirement: JobAssignmentFormRequirement) {
  if (!canFillForms.value) return false
  if (requirement.formSubmissionId !== null) return false

  return !requirement.isDraft()
}

function canViewFormSubmission(requirement: JobAssignmentFormRequirement) {
  if (!canFillForms.value) return false
  return requirement.formSubmissionId !== null
}
</script>

<style scoped></style>
